import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { dataLibraryApi, fetchAllPages, MAX_GROUPED_ROWS } from '../../../services/api/dataLibrary';
import type { DataLibrarySessionFeedbackParams } from '../../../services/api/dataLibrary';
import type {
  DataLibrarySessionFeedbackRow,
  DataLibrarySurveyQuestion,
  DataLibrarySessionFeedbackFilterOptions,
  ColumnAverages,
} from '../../../types';
import type { SortConfig, ColumnConfig } from '../../../hooks/useColumnConfig';
import type { ActiveFilterRow } from '../../common/FilterPanel/FilterPanel';
import type { SortState } from '../../../utils/sortTasks';
import type { GroupState } from '../../common/GroupPanel/GroupPanel';
import { groupDataByLevels, type GroupNode } from '../../../utils/groupDataByField';
import { useDataLibraryColumns } from '../useDataLibraryTableLayout';

interface UseDataLibrarySessionFeedbackProps {
  selectedCohortIds: string[];
  activeGroup: GroupState | null;
  onGroupChange: (group: GroupState | null) => void;
}

const STATIC_COLUMNS: ColumnConfig[] = [
  { id: 'email', label: 'Participant Email', visible: true, sortable: true, groupable: false },
  { id: 'full_name', label: 'Participant Full Name', visible: true, sortable: false, groupable: false },
  { id: 'session_number', label: 'Session Number', visible: true, sortable: true },
  { id: 'session_topic', label: 'Session Topic', visible: true, sortable: false },
  { id: 'session_date', label: 'Session Date', visible: true, sortable: true },
  { id: 'coach_name', label: 'Coach', visible: true, sortable: true },
  { id: 'date_submitted', label: 'Date Submitted', visible: true, sortable: true },
  { id: 'cohort_name', label: 'Cohort', visible: true, sortable: false },
  { id: 'cohort_health', label: 'Cohort Health', visible: false, sortable: false, groupable: false, exportKey: null },
  { id: 'group_name', label: 'Group', visible: false, sortable: false },
  { id: 'overall_satisfaction', label: 'Overall Satisfaction', visible: true, sortable: false },
  // MDF-212: the participant's attendance rate and average engagement, as on the Participants tab.
  { id: 'attendance_rate', label: 'Attendance %', visible: true, sortable: false, groupable: false, exportKey: 'attendance_rate', numeric: 'percent' },
  { id: 'avg_engagement', label: 'Avg Engagement', visible: true, sortable: false, groupable: false, exportKey: 'avg_engagement', numeric: 'decimal' },
  { id: 'has_open_flag', label: 'Flagged', visible: false, sortable: false, groupable: false, exportKey: null },
];

export function useDataLibrarySessionFeedback({
  selectedCohortIds,
  activeGroup,
}: UseDataLibrarySessionFeedbackProps) {
  // Data state
  const [rows, setRows] = useState<DataLibrarySessionFeedbackRow[]>([]);
  const [questions, setQuestions] = useState<DataLibrarySurveyQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  // MDF-41: server averages over all matching rows, by column id
  const [averages, setAverages] = useState<ColumnAverages>({});
  const [totalPages, setTotalPages] = useState(0);
  const [filterOptions, setFilterOptions] = useState<DataLibrarySessionFeedbackFilterOptions>({
    groups: [],
    coaches: [],
    sessionNumbers: [],
  });
  const [hasSurveyConfig, setHasSurveyConfig] = useState(true);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Search
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);

  // Sort — default: session_number DESC, then participant name ASC
  const [sortConfig, setSortConfig] = useState<SortConfig | null>({
    key: 'session_number',
    order: 'DESC',
  });

  // Filters
  const [activeQuickFilters, setActiveQuickFilters] = useState<string[]>([]);
  const [activeFilters, setActiveFilters] = useState<ActiveFilterRow[]>([]);

  // Column config
  const defaultColumns: ColumnConfig[] = useMemo(() => {
    const baseStatic = [...STATIC_COLUMNS];

    const GROUPABLE_QUESTION_TYPES = new Set(['choices', 'yesNo', 'scale']);
    const questionCols: ColumnConfig[] = questions.map(q => ({
      id: `q_${q.question_id}`,
      label: q.question_text,
      visible: true,
      sortable: false,
      groupable: GROUPABLE_QUESTION_TYPES.has(q.question_type),
    }));

    return [...baseStatic, ...questionCols];
  }, [questions]);

  // MDF-209: saved order and visibility for this tab
  const { columns, setColumns } = useDataLibraryColumns(defaultColumns);

  const visibleColumns = useMemo(() => columns.filter(c => c.visible), [columns]);

  // Build API params
  const buildParams = useCallback((): DataLibrarySessionFeedbackParams => {
    const params: DataLibrarySessionFeedbackParams = {
      cohortIds: selectedCohortIds,
      page,
      limit: pageSize,
      ...(sortConfig && { sortBy: sortConfig.key, sortOrder: sortConfig.order }),
    };

    if (debouncedSearch) params.search = debouncedSearch;
    if (activeQuickFilters.includes('flagged')) params.flaggedOnly = true;
    if (activeQuickFilters.includes('absences')) params.hasAbsences = true;

    for (const filter of activeFilters) {
      if (filter.values.length === 0) continue;
      switch (filter.key) {
        case 'group':
          params.groupId = filter.values.join(',');
          break;
        case 'coach':
          params.coachId = filter.values[0];
          break;
        case 'session_number':
          params.sessionNumber = filter.values.join(',');
          break;
      }
    }

    return params;
  }, [selectedCohortIds, page, pageSize, sortConfig, debouncedSearch, activeQuickFilters, activeFilters]);

  // MDF-39: the filtered total when it is over MAX_GROUPED_ROWS; the table then stays paged
  const [overGroupLimit, setOverGroupLimit] = useState<number | null>(null);
  const isGrouped = !!activeGroup && overGroupLimit === null;
  // MDF-39: only the newest request may write state; a grouped load spans many requests
  const latestRequest = useRef(0);

  // Fetch
  const fetchData = useCallback(async () => {
    const requestId = ++latestRequest.current;
    if (selectedCohortIds.length === 0) {
      setRows([]);
      setQuestions([]);
      setTotalCount(0);
      setAverages({});
      setTotalPages(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = buildParams();
      // MDF-39: a grouped table needs every row, so it fetches all pages
      const result = isGrouped
        ? await fetchAllPages((page, limit) => dataLibraryApi.getSessionFeedback({ ...params, page, limit }), () => requestId !== latestRequest.current)
        : await dataLibraryApi.getSessionFeedback(params);
      if (requestId !== latestRequest.current) return;
      const total = result.meta.pagination.total;
      setOverGroupLimit(total > MAX_GROUPED_ROWS ? total : null);
      if (isGrouped && total > MAX_GROUPED_ROWS) return;

      setRows(result.data || []);
      setQuestions(result.meta.questions || []);
      setTotalCount(result.meta.pagination.total);
      setAverages(result.meta.averages);
      setTotalPages(result.meta.pagination.totalPages);
      setFilterOptions(result.meta.filterOptions);
      setHasSurveyConfig(result.meta.hasSurveyConfig);
    } catch {
      if (requestId !== latestRequest.current) return;
      notifications.show({
        title: 'Error',
        message: 'Failed to load session feedback',
        color: 'red',
      });
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [selectedCohortIds, buildParams, isGrouped]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page on filter/search change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeQuickFilters, activeFilters, selectedCohortIds]);

  // Sort handlers
  const handleSort = useCallback((key: string) => {
    setSortConfig(prev => ({
      key,
      order: prev?.key === key && prev.order === 'ASC' ? 'DESC' : 'ASC',
    }));
  }, []);

  const handleSortPanelChange = useCallback((sort: SortState | null) => {
    if (sort) {
      setSortConfig({ key: sort.field, order: sort.direction === 'asc' ? 'ASC' : 'DESC' });
    } else {
      setSortConfig(null);
    }
  }, []);

  // Filter handlers
  const handleQuickFilterToggle = useCallback((key: string) => {
    setActiveQuickFilters(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key],
    );
  }, []);

  const handleFilterAdd = useCallback((key: string) => {
    setActiveFilters(prev => {
      if (prev.some(f => f.key === key)) return prev;
      return [...prev, { key, values: [] }];
    });
  }, []);

  const handleFilterChange = useCallback((key: string, values: string[]) => {
    setActiveFilters(prev => prev.map(f => (f.key === key ? { ...f, values } : f)));
  }, []);

  const handleFilterRemove = useCallback((key: string) => {
    setActiveFilters(prev => prev.filter(f => f.key !== key));
  }, []);

  const clearAllFilters = useCallback(() => {
    setActiveQuickFilters([]);
    setActiveFilters([]);
    setSearchValue('');
  }, []);

  const hasActiveFilters =
    activeQuickFilters.length > 0 ||
    activeFilters.some(f => f.values.length > 0) ||
    debouncedSearch.length > 0;

  // Client-side filters (overall_satisfaction)
  const filteredRows = useMemo(() => {
    let result = rows;

    for (const filter of activeFilters) {
      if (filter.values.length === 0) continue;
      if (filter.key === 'overall_satisfaction') {
        result = result.filter(r => r.overall_satisfaction && filter.values.includes(r.overall_satisfaction));
      }
    }

    return result;
  }, [rows, activeFilters]);

  // Client-side grouping
  const groupedRows = useMemo(() => {
    return groupDataByLevels(
      filteredRows as unknown as Record<string, unknown>[],
      isGrouped ? activeGroup : null,
      columns,
    ) as GroupNode<DataLibrarySessionFeedbackRow>[] | null;
  }, [filteredRows, activeGroup, isGrouped, columns]);

  return {
    groupLimitTotal: activeGroup ? overGroupLimit : null,
    rows: filteredRows,
    groupedRows,
    hasSurveyConfig,
    questions,
    loading,
    totalCount,
    averages,
    totalPages,
    filterOptions,
    page,
    setPage,
    pageSize,
    setPageSize: (size: number) => {
      setPageSize(size);
      setPage(1);
    },
    searchValue,
    setSearchValue,
    sortConfig,
    handleSort,
    handleSortPanelChange,
    activeQuickFilters,
    handleQuickFilterToggle,
    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    clearAllFilters,
    hasActiveFilters,
    columns,
    setColumns,
    visibleColumns,
  };
}
